import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useDirtyGuard } from "@/components/admin/DirtyGuard";

/**
 * Sticky bottom save bar. Shows dirty indicator + last saved time; registers with the shell’s dirty guard.
 */
export const SaveBar = ({ dirty = false, saving = false, onSave, onCancel, lastSaved, saveLabel = "Lagre endringar", cancelLabel = "Forkast", disabled = false, className, testId = "admin-save-bar", children, allowCleanSave = false, saveTestId = "form-save-button", cancelTestId = "form-cancel-button" }) => {
  useDirtyGuard(dirty, testId);
  return (
    <div className={cn("sticky bottom-0 z-20 -mx-4 mt-6 border-t border-border bg-background/90 px-4 py-3 backdrop-blur safe-bottom sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8", className)} data-testid={testId} role="region" aria-label="Lagring">
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 text-[13px]" aria-live="polite">
          {dirty ? (
            <><span className="h-2 w-2 shrink-0 rounded-full bg-sol" aria-hidden="true" /><span className="font-medium" data-testid="form-dirty-indicator">Ulagra endringar</span></>
          ) : (
            <><span className="h-2 w-2 shrink-0 rounded-full bg-status-confirmed" aria-hidden="true" /><span className="text-muted-foreground" data-testid="form-clean-indicator">Alt lagra</span></>
          )}
          {lastSaved && <span className="hidden text-muted-foreground tabular sm:inline" data-testid="form-last-saved">· sist lagra kl. {format(lastSaved, "HH:mm")}</span>}
          {children}
        </div>
        <div className="flex items-center gap-2">
          {dirty && onCancel && <Button variant="ghost" className="h-9" onClick={onCancel} disabled={saving} data-testid={cancelTestId}>{cancelLabel}</Button>}
          <Button className="h-9 min-w-[132px]" onClick={onSave} disabled={saving || disabled || (!dirty && !allowCleanSave)} data-testid={saveTestId}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Draft state for a form backed by server data.
 * - `dirty` = deep-compare against last committed base
 * - adopts new `initial` from the server only while clean
 * - `commit()` after a successful save; `reset()` to discard
 */
export const useDraft = (initial) => {
  const [form, setForm] = useState(initial ?? null);
  const [base, setBase] = useState(() => JSON.stringify(initial ?? null));
  const [lastSaved, setLastSaved] = useState(null);
  const serialized = useMemo(() => JSON.stringify(form ?? null), [form]);
  const dirty = form != null && serialized !== base;

  useEffect(() => {
    const s = JSON.stringify(initial ?? null);
    if (s === base) return;
    if (!dirty) { setForm(initial ?? null); setBase(s); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const set = useCallback((k) => (e) => setForm((f) => ({ ...(f || {}), [k]: e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e })), []);
  const patch = useCallback((obj) => setForm((f) => ({ ...(f || {}), ...obj })), []);
  const reset = useCallback(() => setForm(JSON.parse(base)), [base]);
  const commit = useCallback((next) => {
    setForm((f) => { const v = next === undefined ? f : next; setBase(JSON.stringify(v ?? null)); return v; });
    setLastSaved(new Date());
  }, []);
  /** Mark `sent` as the saved baseline without touching what the user has typed since (autosave). */
  const commitBase = useCallback((sent) => { setBase(JSON.stringify(sent ?? null)); setLastSaved(new Date()); }, []);

  return { form, setForm, set, patch, dirty, reset, commit, commitBase, lastSaved };
};
